<template>
  <settings-menu-layout>
    <row v-if="project">
      <loader :loading="loading"></loader>
      <v-container>
        <col12>
          <!-- Change Project Name Card -->
          <v-card>
            <v-card-title>
              <h2 class="headline">{{$t('settings_project.change_name_title')}}</h2>
            </v-card-title>
            <v-card-text>
              <form>
                <v-text-field
                v-model="name"
                :counter="255"
                :label="$t('settings_project.name')"
                required
              ></v-text-field>
              <v-text-field
                v-model="description"
                :label="$t('settings_project.description')"
                required
              ></v-text-field>
              <v-btn
                color="primary"
                class="mr-4"
                @click="save"
                :disabled="disable_save"
              >
                {{$t('settings_project.btn_save')}}
              </v-btn>
              <v-btn color="primary" class="mr-4" @click="clear">
                {{$t('settings_project.btn_clear')}}
              </v-btn>
              <v-btn color="primary" class="mr-4" @click="reset">
                {{$t('settings_project.btn_reset')}}
              </v-btn>
              </form>
            </v-card-text>
          </v-card>
        </col12>
      </v-container>

      <v-container>
        <col12>
          <v-card>
            <v-card-title>
              <h2 class="headline">{{ $t('settings_project.upload_settings_title') }}</h2>
            </v-card-title>
            <v-card-text>
              <v-switch
                v-model="autoValidationSplitEnabled"
                :label="$t('settings_project.auto_validation_split_label')"
                :hint="$t('settings_project.auto_validation_split_hint')"
                persistent-hint
                color="primary"
                :disabled="settingsLoading"
                @update:model-value="saveAutoValidationSetting"
              />
            </v-card-text>
          </v-card>
        </col12>
      </v-container>

      <v-container>
        <col12>
          <v-card>
            <v-card-title>
              <h2 class="headline">{{ $t('settings_project.sensitive_image_title') }}</h2>
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-4">{{ $t('settings_project.sensitive_image_intro') }}</p>
              <v-switch
                v-model="blurUnlessMetaEnabled"
                :label="$t('settings_project.sensitive_image_switch')"
                :hint="$t('settings_project.sensitive_image_switch_hint')"
                persistent-hint
                color="primary"
                :disabled="settingsLoading"
                @update:model-value="saveBlurUnlessMetaEnabled"
              />
              <v-text-field
                v-model="blurUnlessMetaKey"
                class="mt-4"
                :label="$t('settings_project.sensitive_image_meta_key')"
                :disabled="settingsLoading"
                density="comfortable"
                hide-details="auto"
              />
              <v-text-field
                v-model="blurUnlessMetaValue"
                class="mt-2"
                :label="$t('settings_project.sensitive_image_meta_value')"
                :hint="$t('settings_project.sensitive_image_meta_value_hint')"
                persistent-hint
                :disabled="settingsLoading"
                density="comfortable"
              />
              <v-btn
                color="primary"
                class="mt-2"
                :disabled="settingsLoading"
                @click="saveBlurUnlessMetaRule"
              >
                {{ $t('settings_project.sensitive_image_save_rule') }}
              </v-btn>

              <v-divider class="my-6" />

              <p class="text-subtitle-1 font-weight-medium mb-2">
                {{ $t('settings_project.bbox_blur_section_title') }}
              </p>
              <p class="text-body-2 mb-4">{{ $t('settings_project.bbox_blur_intro') }}</p>
              <v-switch
                v-model="bboxBlurEnabled"
                :label="$t('settings_project.bbox_blur_switch')"
                :hint="$t('settings_project.bbox_blur_switch_hint')"
                persistent-hint
                color="primary"
                :disabled="settingsLoading"
                @update:model-value="saveBBoxBlurEnabled"
              />
              <BboxBlurLabelSelect
                v-model="bboxBlurLabels"
                class="mt-4"
                test-id="bbox-blur-labels-select"
                :field-label="$t('settings_project.bbox_blur_labels')"
                :hint="$t('settings_project.bbox_blur_labels_hint')"
                :search-placeholder="$t('filter_tree.label_search_placeholder')"
                :disabled="settingsLoading"
              />
              <BboxBlurLabelSelect
                v-model="bboxBlurUnblurLabels"
                class="mt-4"
                test-id="bbox-blur-unblur-labels-select"
                :field-label="$t('settings_project.bbox_blur_unblur_labels')"
                :hint="$t('settings_project.bbox_blur_unblur_labels_hint')"
                :search-placeholder="$t('filter_tree.label_search_placeholder')"
                :disabled="settingsLoading"
                :allow-all-other="true"
                :all-other-label="$t('settings_project.bbox_blur_all_other_labels')"
              />
              <v-btn
                color="primary"
                class="mt-2"
                :disabled="settingsLoading"
                @click="saveBBoxBlurLabels"
              >
                {{ $t('settings_project.bbox_blur_save_labels') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </col12>
      </v-container>

      <v-container>
        <col12>
          <v-card data-testid="asset-grid-meta-settings">
            <v-card-title>
              <h2 class="headline">{{ $t('settings_project.grid_meta_title') }}</h2>
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-4">{{ $t('settings_project.grid_meta_intro') }}</p>
              <v-combobox
                v-model="assetGridMetaFields"
                data-testid="asset-grid-meta-fields"
                :label="$t('settings_project.grid_meta_fields')"
                :hint="$t('settings_project.grid_meta_fields_hint')"
                persistent-hint
                multiple
                chips
                closable-chips
                :delimiters="[',']"
                :disabled="settingsLoading"
                density="comfortable"
                hide-details="auto"
                class="mb-2"
              />
              <v-btn
                color="primary"
                class="mt-3"
                data-testid="asset-grid-meta-save"
                :disabled="settingsLoading || assetGridMetaSaving"
                :loading="assetGridMetaSaving"
                @click="saveAssetGridMetaFields"
              >
                {{ $t('settings_project.grid_meta_save') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </col12>
      </v-container>

      <v-container>
        <col12>
          <v-card data-testid="llm-endpoint-settings">
            <v-card-title>
              <h2 class="headline">{{ $t('settings_project.llm_endpoint_title') }}</h2>
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-4">{{ $t('settings_project.llm_endpoint_intro') }}</p>
              <v-text-field
                v-model="llmModelUrl"
                data-testid="llm-model-url"
                :label="$t('settings_project.llm_endpoint_url')"
                :hint="$t('settings_project.llm_endpoint_url_hint')"
                persistent-hint
                :disabled="settingsLoading"
                density="comfortable"
                hide-details="auto"
                class="mb-2"
              />
              <v-text-field
                v-model="llmModelToken"
                data-testid="llm-model-token"
                :label="$t('settings_project.llm_endpoint_token')"
                :hint="$t('settings_project.llm_endpoint_token_hint')"
                persistent-hint
                type="password"
                autocomplete="new-password"
                :disabled="settingsLoading"
                density="comfortable"
                hide-details="auto"
                class="mt-2"
              />
              <v-btn
                color="primary"
                class="mt-3"
                data-testid="llm-endpoint-save"
                :disabled="settingsLoading || llmEndpointSaving"
                :loading="llmEndpointSaving"
                @click="saveLlmEndpoint"
              >
                {{ $t('settings_project.llm_endpoint_save') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </col12>
      </v-container>

      <v-container>
        <col12>
          <v-card>
            <v-card-title>
              <h2 class="headline">{{ $t('settings_project.upload_tasks_title') }}</h2>
            </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-4">{{ $t('settings_project.upload_tasks_intro') }}</p>

              <on-upload-pipeline-editor
                ref="uploadPipelineEditor"
                v-model="onUploadPipeline"
                :models="models"
                :models-loading="modelsLoading"
                :disabled="settingsLoading"
                :can-configure-llm="can_configure_auto_analyse"
              />

              <v-btn
                color="primary"
                class="mt-4"
                data-testid="upload-automation-save"
                :disabled="settingsLoading || uploadAutomationSaving"
                :loading="uploadAutomationSaving"
                @click="saveUploadAutomation"
              >
                {{ $t('settings_project.upload_tasks_save') }}
              </v-btn>
            </v-card-text>
          </v-card>
        </col12>
      </v-container>

      <v-container>
        <col12>
          <!-- Delete Project Card -->
          <v-card>
            <confirm-delete-modal
              :value="project.name"
              @confirmed="delete_project"
              @close_modal="close_confirm_modal"
              v-if="show_confirm_modal"
              title="Confirm delete project"
            ></confirm-delete-modal>
            <v-card-title>
              <h2 class="headline">{{$t('settings_project.delete_project_title')}}</h2>
            </v-card-title>
            <v-card-text>
              <p class="subheading">
                {{$t('settings_project.delete_project_additional_info')}}
              </p>
              <v-btn
                color="error"
                class="mr-4"
                @click="confirm_delete_project"
              >
                {{$t('settings_project.btn_delete')}}
              </v-btn>
            </v-card-text>
          </v-card>
        </col12>
      </v-container>
    </row>
  </settings-menu-layout>
</template>


<script>
  import SettingsMenuLayout from "@/components/layout/SettingsMenuLayout";
  import LoadProjectsMixin from "@/mixins/LoadProjectsMixin";
  import Row from "@/components/layout/Row";
  import Col12 from "@/components/layout/Col12";
  import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
  import Loader from "@/components/layout/loader";
  import LoadUsersMixin from "@/mixins/LoadUsersMixin";
  import LoadModelsMixin from "@/mixins/LoadModelsMixin";
  import CurrentUserMixin from "@/mixins/CurrentUserMixin";
  import BboxBlurLabelSelect from "@/components/BboxBlurLabelSelect.vue";
  import OnUploadPipelineEditor, {
    hydratePipelineSteps,
    serializePipelineSteps,
  } from "@/components/settings/OnUploadPipelineEditor.vue";
  import {
    BLUR_UNLESS_META_ENABLED,
    BLUR_UNLESS_META_KEY,
    BLUR_UNLESS_META_VALUE,
    BBOX_BLUR_ENABLED,
    BBOX_BLUR_LABELS,
    BBOX_BLUR_UNBLUR_LABELS,
    BBOX_BLUR_UNBLUR_ALL_OTHER,
    parseLabelListSetting,
  } from '@/utils/sensitiveImageBlur';
  import {
    ASSET_GRID_META_FIELDS,
    getAssetGridMetaFieldsFromSettings,
    normalizeMetaFieldKeys,
  } from '@/utils/assetGridMeta';

  const ON_UPLOAD_RUN_MODEL_MODE = 'on_upload_run_model_mode';
  const ON_UPLOAD_LLM_JSON = 'on_upload_llm_json';
  const ON_UPLOAD_PIPELINE_JSON = 'on_upload_pipeline_json';
  const LLM_MODEL_URL = 'llm_model_url';
  const LLM_MODEL_TOKEN = 'llm_model_token';

  function defaultOnUploadLlm() {
    return {
      enabled: false,
      do_find: false,
      do_verify: false,
      do_llm_question: false,
      do_llm_prompt_boxes: false,
      apply_privacy_blur: false,
      find_query: '',
      category_label_id: null,
      llm_question: '',
      llm_question_meta_key: '',
      llm_prompt_boxes_text: '',
      llm_prompt_boxes_label_id: null,
    };
  }

  function pipelineFromLegacy(modeRaw, llmRaw) {
    const steps = [];
    const mode = (modeRaw || '').trim();
    const lower = mode.toLowerCase();
    if (lower && lower !== 'none' && lower !== 'false' && lower !== '0') {
      if (lower === 'all_active') {
        steps.push({ type: 'all_active' });
      } else {
        const modelId = parseInt(mode, 10);
        if (modelId > 0) {
          steps.push({ type: 'model', model_id: modelId });
        }
      }
    }
    let llm = defaultOnUploadLlm();
    if (llmRaw && String(llmRaw).trim()) {
      try {
        const parsed = JSON.parse(llmRaw);
        if (parsed && typeof parsed === 'object') {
          llm = { ...llm, ...parsed };
        }
      } catch (_) { /* keep defaults */ }
    }
    if (llm.enabled && (llm.do_find || llm.do_verify || llm.do_llm_question || llm.do_llm_prompt_boxes)) {
      const applyBlur = Object.prototype.hasOwnProperty.call(llm, 'apply_privacy_blur')
        ? !!llm.apply_privacy_blur
        : !!llm.do_llm_question;
      steps.push({
        type: 'llm',
        do_find: !!llm.do_find,
        do_verify: !!llm.do_verify,
        do_llm_question: !!llm.do_llm_question,
        do_llm_prompt_boxes: !!llm.do_llm_prompt_boxes,
        apply_privacy_blur: applyBlur,
        find_query: llm.find_query || '',
        category_label_id: llm.category_label_id != null ? Number(llm.category_label_id) : null,
        llm_question: llm.llm_question || '',
        llm_question_meta_key: llm.llm_question_meta_key || '',
        llm_prompt_boxes_text: llm.llm_prompt_boxes_text || '',
        llm_prompt_boxes_label_id: llm.llm_prompt_boxes_label_id != null
          ? Number(llm.llm_prompt_boxes_label_id)
          : null,
      });
    }
    return serializePipelineSteps(hydratePipelineSteps(steps));
  }

  /** Mirror pipeline into legacy settings for simple-mode / older readers. */
  function legacyFromPipeline(steps) {
    const list = Array.isArray(steps) ? steps : [];
    let mode = 'none';
    const modelSteps = list.filter((s) => s.type === 'model');
    const allActive = list.some((s) => s.type === 'all_active');
    if (allActive && modelSteps.length === 0 && list.filter((s) => s.type !== 'llm').length === 1) {
      mode = 'all_active';
    } else if (modelSteps.length === 1 && !allActive && list.filter((s) => s.type === 'model' || s.type === 'all_active').length === 1) {
      mode = String(modelSteps[0].model_id);
    } else if (list.some((s) => s.type === 'model' || s.type === 'all_active')) {
      // Multi-step / mixed — pipeline JSON is source of truth; disable legacy model mode.
      mode = 'none';
    }
    const llmStep = list.find((s) => s.type === 'llm');
    const llm = defaultOnUploadLlm();
    if (llmStep) {
      llm.enabled = true;
      llm.do_find = !!llmStep.do_find;
      llm.do_verify = !!llmStep.do_verify;
      llm.do_llm_question = !!llmStep.do_llm_question;
      llm.do_llm_prompt_boxes = !!llmStep.do_llm_prompt_boxes;
      llm.apply_privacy_blur = !!llmStep.apply_privacy_blur;
      llm.find_query = llmStep.find_query || '';
      llm.category_label_id = llmStep.category_label_id != null ? Number(llmStep.category_label_id) : null;
      llm.llm_question = llmStep.llm_question || '';
      llm.llm_question_meta_key = llmStep.llm_question_meta_key || '';
      llm.llm_prompt_boxes_text = llmStep.llm_prompt_boxes_text || '';
      llm.llm_prompt_boxes_label_id = llmStep.llm_prompt_boxes_label_id != null
        ? Number(llmStep.llm_prompt_boxes_label_id)
        : null;
    }
    return { mode, llm };
  }

  export default {
    name: "SettingsProject",
    mixins: [LoadProjectsMixin, LoadUsersMixin, LoadModelsMixin, CurrentUserMixin],
    data() {
      return {
        description: false,
        name: false,
        show_confirm_modal: false,
        loading: false,
        settingsLoading: true,
        autoValidationSplitEnabled: true,
        autoValidationSettingName: 'auto_validation_split_percent',
        blurUnlessMetaEnabled: false,
        blurUnlessMetaKey: 'patient_data_visible',
        blurUnlessMetaValue: 'true',
        bboxBlurEnabled: false,
        bboxBlurLabels: [],
        bboxBlurUnblurLabels: [],
        onUploadPipeline: [],
        uploadAutomationSaving: false,
        llmModelUrl: '',
        llmModelToken: '',
        llmEndpointSaving: false,
        assetGridMetaFields: [],
        assetGridMetaSaving: false,
      }
    },
    methods: {
      set_loading(loading) {
        this.loading = loading
      },
      save() {
        let project = JSON.parse(JSON.stringify(this.project));
        project.name = this.name;
        project.description = this.description;
        this.$store.dispatch('project/patch', { item: project, dispatch: this.$store.dispatch });
      },
      close_confirm_modal () {
        this.show_confirm_modal = false;
      },
      confirm_delete_project () {
        this.show_confirm_modal = true;
      },
      delete_project() {
        this.loading = true;
        this.$store.dispatch('project/delete', this.project);
        this.$router.push({name:'Projects'});
      },
      reset() {
        this.name = this.project.name;
        this.description = this.project.description;
      },
      clear() {
        this.name = '';
        this.description = '';
      },
      init() {
        this.name = this.project.name;
        this.description = this.project.description;
        this.loadUploadSettings();
      },
      async loadUploadSettings() {
        this.settingsLoading = true;
        try {
          const settings = await this.$api.setting.all();
          const row = (settings || []).find(
            (s) => s && s.name === this.autoValidationSettingName,
          );
          if (!row || row.value === '' || row.value == null) {
            this.autoValidationSplitEnabled = true;
          } else {
            this.autoValidationSplitEnabled = parseInt(row.value, 10) > 0;
          }

          const en = (settings || []).find((s) => s && s.name === BLUR_UNLESS_META_ENABLED);
          this.blurUnlessMetaEnabled = Boolean(
            en && (en.value === '1' || en.value === 1 || String(en.value).toLowerCase() === 'true'),
          );
          const kr = (settings || []).find((s) => s && s.name === BLUR_UNLESS_META_KEY);
          const vr = (settings || []).find((s) => s && s.name === BLUR_UNLESS_META_VALUE);
          if (kr && kr.value != null && String(kr.value).trim() !== '') {
            this.blurUnlessMetaKey = String(kr.value).trim();
          }
          if (vr && vr.value != null && String(vr.value).trim() !== '') {
            this.blurUnlessMetaValue = String(vr.value).trim();
          }

          const bboxEn = (settings || []).find((s) => s && s.name === BBOX_BLUR_ENABLED);
          this.bboxBlurEnabled = Boolean(
            bboxEn && (bboxEn.value === '1' || bboxEn.value === 1 || String(bboxEn.value).toLowerCase() === 'true'),
          );
          const bboxBlurRow = (settings || []).find((s) => s && s.name === BBOX_BLUR_LABELS);
          const bboxUnblurRow = (settings || []).find((s) => s && s.name === BBOX_BLUR_UNBLUR_LABELS);
          this.bboxBlurLabels = parseLabelListSetting(bboxBlurRow && bboxBlurRow.value);
          this.bboxBlurUnblurLabels = parseLabelListSetting(bboxUnblurRow && bboxUnblurRow.value);

          const modeRow = (settings || []).find((s) => s && s.name === ON_UPLOAD_RUN_MODEL_MODE);
          const rawMode = modeRow && modeRow.value != null ? String(modeRow.value).trim() : '';
          const llmRow = (settings || []).find((s) => s && s.name === ON_UPLOAD_LLM_JSON);
          const llmRaw = llmRow && llmRow.value != null ? String(llmRow.value) : '';
          const pipeRow = (settings || []).find((s) => s && s.name === ON_UPLOAD_PIPELINE_JSON);
          const pipeRaw = pipeRow && pipeRow.value != null ? String(pipeRow.value).trim() : '';
          if (pipeRaw && pipeRaw !== '[]' && pipeRaw !== 'null') {
            try {
              const parsed = JSON.parse(pipeRaw);
              this.onUploadPipeline = serializePipelineSteps(hydratePipelineSteps(parsed));
            } catch (_) {
              this.onUploadPipeline = pipelineFromLegacy(rawMode, llmRaw);
            }
          } else {
            this.onUploadPipeline = pipelineFromLegacy(rawMode, llmRaw);
          }

          const llmUrlRow = (settings || []).find((s) => s && s.name === LLM_MODEL_URL);
          this.llmModelUrl = llmUrlRow && llmUrlRow.value != null
            ? String(llmUrlRow.value).trim()
            : '';
          const llmTokenRow = (settings || []).find((s) => s && s.name === LLM_MODEL_TOKEN);
          this.llmModelToken = llmTokenRow && llmTokenRow.value != null
            ? String(llmTokenRow.value)
            : '';

          this.assetGridMetaFields = getAssetGridMetaFieldsFromSettings(settings || []);
        } finally {
          this.settingsLoading = false;
        }
      },
      async saveBlurUnlessMetaEnabled(enabled) {
        const value = enabled ? '1' : '0';
        await this.$api.setting.patch({
          name: BLUR_UNLESS_META_ENABLED,
          value,
        });
        await this.$store.dispatch('setting/all', { force_reload: true });
      },
      async saveBlurUnlessMetaRule() {
        const key = (this.blurUnlessMetaKey || '').trim();
        const val = (this.blurUnlessMetaValue || '').trim();
        if (!key) {
          return;
        }
        await this.$api.setting.patch({
          name: BLUR_UNLESS_META_KEY,
          value: key,
        });
        await this.$api.setting.patch({
          name: BLUR_UNLESS_META_VALUE,
          value: val || 'true',
        });
        await this.$store.dispatch('setting/all', { force_reload: true });
      },
      async saveBBoxBlurEnabled(enabled) {
        await this.$api.setting.patch({
          name: BBOX_BLUR_ENABLED,
          value: enabled ? '1' : '0',
        });
        await this.$store.dispatch('setting/all', { force_reload: true });
      },
      normalizeLabelChips(list) {
        const seen = new Set();
        const out = [];
        (list || []).forEach((item) => {
          const name = typeof item === 'string'
            ? item.trim()
            : (item && item.name != null ? String(item.name).trim() : String(item).trim());
          if (!name) {
            return;
          }
          const key = name.toLowerCase();
          if (seen.has(key)) {
            return;
          }
          seen.add(key);
          out.push(name);
        });
        // All-other sentinel replaces any concrete keep-visible labels.
        if (out.some((n) => n.toLowerCase() === BBOX_BLUR_UNBLUR_ALL_OTHER.toLowerCase())) {
          return [BBOX_BLUR_UNBLUR_ALL_OTHER];
        }
        return out;
      },
      async saveBBoxBlurLabels() {
        const blurLabels = this.normalizeLabelChips(this.bboxBlurLabels)
          .filter((n) => n.toLowerCase() !== BBOX_BLUR_UNBLUR_ALL_OTHER.toLowerCase());
        const unblurLabels = this.normalizeLabelChips(this.bboxBlurUnblurLabels);
        this.bboxBlurLabels = blurLabels;
        this.bboxBlurUnblurLabels = unblurLabels;
        await this.$api.setting.patch({
          name: BBOX_BLUR_LABELS,
          value: JSON.stringify(blurLabels),
        });
        await this.$api.setting.patch({
          name: BBOX_BLUR_UNBLUR_LABELS,
          value: JSON.stringify(unblurLabels),
        });
        await this.$store.dispatch('setting/all', { force_reload: true });
      },
      async saveAutoValidationSetting(enabled) {
        const value = enabled ? '20' : '0';
        await this.$api.setting.patch({
          name: this.autoValidationSettingName,
          value,
        });
      },
      async saveLlmEndpoint() {
        this.llmEndpointSaving = true;
        try {
          const url = (this.llmModelUrl || '').trim();
          const token = (this.llmModelToken || '').trim();
          this.llmModelUrl = url;
          this.llmModelToken = token;
          await this.$api.setting.patch({
            name: LLM_MODEL_URL,
            value: url,
          });
          await this.$api.setting.patch({
            name: LLM_MODEL_TOKEN,
            value: token,
          });
          await this.$store.dispatch('setting/all', { force_reload: true });
          this.$store.dispatch(
            'snackbars/add_success_snack',
            this.$t('settings_project.llm_endpoint_saved'),
          );
        } catch (_) {
          this.$store.dispatch(
            'snackbars/add_failed_snack',
            this.$t('settings_project.llm_endpoint_save_failed'),
          );
        } finally {
          this.llmEndpointSaving = false;
        }
      },
      async saveAssetGridMetaFields() {
        this.assetGridMetaSaving = true;
        try {
          const keys = normalizeMetaFieldKeys(this.assetGridMetaFields);
          this.assetGridMetaFields = keys;
          await this.$api.setting.patch({
            name: ASSET_GRID_META_FIELDS,
            value: JSON.stringify(keys),
          });
          await this.$store.dispatch('setting/all', { force_reload: true });
          this.$store.dispatch(
            'snackbars/add_success_snack',
            this.$t('settings_project.grid_meta_saved'),
          );
        } catch (_) {
          this.$store.dispatch(
            'snackbars/add_failed_snack',
            this.$t('settings_project.grid_meta_save_failed'),
          );
        } finally {
          this.assetGridMetaSaving = false;
        }
      },
      validateOnUploadPipeline() {
        const editor = this.$refs.uploadPipelineEditor;
        if (!editor || typeof editor.validate !== 'function') {
          return true;
        }
        const errKey = editor.validate();
        if (!errKey) {
          return true;
        }
        this.$store.dispatch('snackbars/add_failed_snack', this.$t(errKey));
        return false;
      },
      async saveUploadAutomation() {
        if (!this.validateOnUploadPipeline()) {
          return;
        }
        this.uploadAutomationSaving = true;
        try {
          const steps = serializePipelineSteps(hydratePipelineSteps(this.onUploadPipeline));
          this.onUploadPipeline = steps;
          await this.$api.setting.patch({
            name: ON_UPLOAD_PIPELINE_JSON,
            value: JSON.stringify(steps),
          });
          const { mode, llm } = legacyFromPipeline(steps);
          await this.$api.setting.patch({
            name: ON_UPLOAD_RUN_MODEL_MODE,
            value: mode,
          });
          await this.$api.setting.patch({
            name: ON_UPLOAD_LLM_JSON,
            value: JSON.stringify(llm),
          });
          await this.$store.dispatch('setting/all', { force_reload: true });
          this.$store.dispatch('snackbars/add_success_snack', this.$t('settings_project.upload_tasks_saved'));
        } catch (_) {
          this.$store.dispatch('snackbars/add_failed_snack', this.$t('settings_project.upload_tasks_save_failed'));
        } finally {
          this.uploadAutomationSaving = false;
        }
      },
    },
    computed: {
      modelsLoading() {
        return !this.models_loaded;
      },
      disable_save() {
        return !this.name || !this.description || (this.name === this.project.name && this.description === this.project.description);
      },
      can_configure_auto_analyse() {
        return this.is_admin || this.is_manager || this.is_global_admin;
      },
      project() {
        let project_id = this.$store.getters['user/get_current_project_id']
        let ids = this.projects.map(project=>{return project.id});
        let index = ids.indexOf(parseInt(project_id))
        return this.projects[index];
      }
    },
    components: {
      Loader,
      ConfirmDeleteModal,
      Col12,
      Row,
      SettingsMenuLayout,
      BboxBlurLabelSelect,
      OnUploadPipelineEditor,
    },
    watch: {
      project(n) {
        if(n) {
          this.init();
        }
      },
    },
    mounted() {
      if(this.project) {
        this.init();
      }
    },
  }
</script>

<style scoped>

</style>
